// utils/tokenUtils.js
import axios from "axios";
import { API_ESA_OFFICE } from "./index";
import { forceLogoutInactive } from "./forceLogoutInactive";
import {
  isNetworkOrServerError,
  isInactiveAccountError,
} from "./networkErrorUtils";

// Cek apakah token expired
export const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    const decoded = JSON.parse(atob(token.split(".")[1]));
    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  } catch (error) {
    return true;
  }
};

// Cooldown (ms) setelah refresh gagal karena jaringan - hindari loop/spam request
const REFRESH_NETWORK_ERROR_COOLDOWN_MS = 60000; // 60 detik
let lastRefreshNetworkErrorAt = 0;

// Custom error untuk cooldown (agar pemanggil bisa membedakan)
export const isRefreshCooldownError = (err) =>
  err?.code === "REFRESH_COOLDOWN" || err?.isRefreshCooldown === true;

// Refresh token function
export const refreshToken = async () => {
  const now = Date.now();
  const inCooldown =
    lastRefreshNetworkErrorAt > 0 &&
    now - lastRefreshNetworkErrorAt < REFRESH_NETWORK_ERROR_COOLDOWN_MS;

  if (inCooldown) {
    const err = new Error("Refresh token in cooldown (network error recently)");
    err.code = "REFRESH_COOLDOWN";
    err.isRefreshCooldown = true;
    throw err;
  }

  try {
    const response = await axios.get(`${API_ESA_OFFICE}users/token`, {
      withCredentials: true,
    });

    if (response.data.accessToken) {
      localStorage.setItem("accessToken", response.data.accessToken);
      lastRefreshNetworkErrorAt = 0;
      return response.data.accessToken;
    }

    throw new Error("No access token received");
  } catch (error) {
    if (isNetworkOrServerError(error)) {
      lastRefreshNetworkErrorAt = Date.now();
    } else {
      console.error("❌ Refresh token failed:", error);
      localStorage.removeItem("accessToken");
      localStorage.removeItem("user");
    }
    throw error;
  }
};

// Get valid access token (refresh if needed)
export const getValidAccessToken = async () => {
  let accessToken = localStorage.getItem("accessToken");

  // Jika tidak ada token atau expired, coba refresh
  if (!accessToken || isTokenExpired(accessToken)) {
    try {
      accessToken = await refreshToken();
    } catch (error) {
      throw new Error("Authentication failed");
    }
  }

  return accessToken;
};

// Request interceptor (hanya sekali)
let requestInterceptorDone = false;
const setupRequestInterceptor = () => {
  if (requestInterceptorDone) return;
  requestInterceptorDone = true;

  axios.interceptors.request.use(
    (config) => {
      // Jangan tambahkan token untuk endpoint login/refresh
      const url = config.url || "";
      const fullUrl = (config.baseURL && !url.startsWith("http") ? config.baseURL + url : url) || url;
      if (
        url.includes("users/uid/") ||
        url.includes("users/token") ||
        url.includes("authentication/")
      ) {
        return config;
      }
      // Jangan tambahkan token untuk endpoint publik
      if (
        fullUrl.includes("/api/public/") ||
        fullUrl.includes("/dinas/list-publik") ||
        fullUrl.includes("/events/client-ip") ||
        /\/events\/\d+(\/detail)?$/.test(fullUrl) ||
        /\/attendance\/\d+$/.test(fullUrl) ||
        fullUrl.includes("/survey-kepuasan/") ||
        fullUrl.includes("/buku-tamu/buku-dinas/link/") ||
        fullUrl.includes("/buku-tamu/bidang/") ||
        fullUrl.includes("/buku-tamu/users/")
      ) {
        return config;
      }

      const accessToken = localStorage.getItem("accessToken");
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      config.withCredentials = true;
      return config;
    },
    (error) => Promise.reject(error)
  );
};

// Setup axios interceptor untuk handle 401 (hanya sekali - cegah stacking)
let interceptorSetupDone = false;

// Setup axios interceptor untuk handle 401
export const setupAxiosInterceptor = (navigate) => {
  if (interceptorSetupDone) return;
  interceptorSetupDone = true;

  setupRequestInterceptor();

  let isRefreshing = false;
  let failedQueue = [];

  const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });

    failedQueue = [];
  };

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // 403 karena status_EO tidak aktif: logout langsung tanpa dialog konfirmasi
      // KECUALI saat di halaman login - biarkan error sampai ke catch agar bisa
      // menampilkan pesan "Akun Dinonaktifkan" dan menghentikan loading
      if (isInactiveAccountError(error)) {
        const hash = window.location.hash || "";
        const isLoginPage = hash.includes("/authentication/sign-in");
        if (isLoginPage) {
          // Saat login: biarkan error propagate ke catch untuk swal + setLoginProses(false)
          return Promise.reject(error);
        }
        forceLogoutInactive();
        return new Promise(() => {});
      }

      if (error.response?.status === 401 && !originalRequest._retry) {
        const now = Date.now();
        const inCooldown =
          lastRefreshNetworkErrorAt > 0 &&
          now - lastRefreshNetworkErrorAt < REFRESH_NETWORK_ERROR_COOLDOWN_MS;

        if (inCooldown) {
          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return axios(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newAccessToken = await refreshToken();
          lastRefreshNetworkErrorAt = 0;
          processQueue(null, newAccessToken);
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return axios(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          if (isNetworkOrServerError(refreshError)) {
            lastRefreshNetworkErrorAt = Date.now();
            return Promise.reject(refreshError);
          }
          lastRefreshNetworkErrorAt = 0;
          localStorage.removeItem("user");
          localStorage.removeItem("accessToken");
          const hash = window.location.hash || "";
          const isPublicRoute =
            hash.includes("/survey/") ||
            hash.includes("/publik/") ||
            hash.includes("/download/");
          if (!isPublicRoute) {
            if (navigate) {
              navigate("/authentication/sign-in/cover");
            } else {
              window.location.href = "/authentication/sign-in/cover";
            }
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
};

// Make authenticated request
export const makeAuthenticatedRequest = async (url, options = {}) => {
  const accessToken = await getValidAccessToken();

  return axios({
    url,
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${accessToken}`,
    },
    withCredentials: true,
  });
};
